import { readVarM } from '@execonline-inc/environment';
import { trackEvent } from '@intercom/messenger-js-sdk';
import { just, Maybe, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import ExternalLink from '../../../Native/AEP/ExternalLink';
import { ProgramDetailResource, Progress } from '../../../ProgramStore/Types';
import { T } from '../../../Translations';
import { useTranslationsContext } from '../../../Translations/UseTranslationsContext';
import * as style from '../style.module.css';

import type { JSX } from 'react';

interface Props {
  programDetailResource: ProgramDetailResource;
}

const INTERCOM_CLICK_EVENT = 'Segment Overview Panel - View Full Course Page - Click';

const courseOverviewHref = (programId: number): string => {
  const base = readVarM('VITE_BASE_NAME')
    .map((b) => `/${b}`)
    .getOrElseValue('');
  return `${base}/programs/${programId}`;
};

const trackCourseLinkClick = () => {
  try {
    if (typeof window !== 'undefined' && window.Intercom) {
      trackEvent(INTERCOM_CLICK_EVENT);
    }
  } catch (error) {
    console.warn('Failed to track Intercom event:', INTERCOM_CLICK_EVENT, error);
  }
};

const ProgramOverviewTitle: React.FC<Props> = ({ programDetailResource }) => {
  const { translate } = useTranslationsContext();

  const whenProgramProgress = (content: JSX.Element, progress: Progress): Maybe<JSX.Element> => {
    switch (progress.kind) {
      case 'program-progress':
      case 'module-progress':
        return just(content);
      case 'registration-segment-progress':
        return nothing();
    }
  };

  const whenNotRegistering = (content: JSX.Element): Maybe<JSX.Element> => {
    switch (programDetailResource.payload.kind) {
      case 'active':
      case 'completed':
        return whenProgramProgress(content, programDetailResource.payload.progress);
      case 'expired':
      case 'upcoming':
      case 'inactive':
        return just(content);
    }
  };

  return whenNotRegistering(
    <>
      <h3 className={style.supPanelHeader} data-test-outline-header="Overview">
        <T kind="Overview" />
      </h3>
      <ExternalLink
        href={courseOverviewHref(programDetailResource.payload.id)}
        className={style.legendTrigger}
        aria-label={translate('View full course page')}
        title={translate('View full course page')}
        onClick={trackCourseLinkClick}
        data-intercom-target="segment-overview-course-link"
        data-test-course-overview-link={true}
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden={true}>
          <path
            d="M14 4h6m0 0v6m0-6L10 14M20 14v4a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </ExternalLink>
    </>,
  ).getOrElseValue(<></>);
};

export default observer(ProgramOverviewTitle);
